import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Grid,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Image } from "antd";
import { main_button } from "./Styles";
import moment from "moment";
import { Visibility, Close, Download, PictureAsPdf } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { getOrderCourierLabel, getOrderTrackingId } from "../utils/shippingDisplay";

const TransactionDetailsModal = ({
  open,
  onClose,
  transaction,
  order,
  vendor,
  transactions, // Array of transactions for order breakdown
  showProof = true, // For orders, set to false
}) => {
  const { t } = useTranslation();
  const [imageLoading, setImageLoading] = React.useState(false);
  const [pdfUrl, setPdfUrl] = React.useState(null);
  const [showPdfInline, setShowPdfInline] = React.useState(false);

  React.useEffect(() => {
    if (open && transaction?.proof && showProof) {
      const proofUrl = transaction.proof;
      // Check if it's a PDF
      if (proofUrl.toLowerCase().endsWith('.pdf') || proofUrl.includes('.pdf')) {
        setPdfUrl(proofUrl);
      }
      setImageLoading(false);
    } else {
      setPdfUrl(null);
      setImageLoading(false);
    }
    setShowPdfInline(false);
  }, [open, transaction, showProof]);

  const handleDownloadProof = () => {
    if (transaction?.proof) {
      const link = document.createElement('a');
      link.href = transaction.proof;
      link.download = `proof-${transaction._id}.${transaction.proof.toLowerCase().endsWith('.pdf') ? 'pdf' : 'jpg'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const isImage = transaction?.proof && !transaction.proof.toLowerCase().endsWith('.pdf') && !transaction.proof.includes('.pdf');
  const hasProof = showProof && !!transaction?.proof;

  // Calculate net total for orders
  const netTotal = React.useMemo(() => {
    if (!order || !transactions || transactions.length === 0) return 0;
    return transactions.reduce((sum, t) => {
      const value = parseFloat(t.value || 0);
      if (t.type === 'COD') {
        // COD can be positive (payment) or negative (refund) - add value as-is
        return sum + value;
      } else if (t.type === 'Compensation') {
        // Compensation is always positive (credit)
        return sum + value;
      } else if (['Fee', 'ShippingFee'].includes(t.type)) {
        // Fees are always negative (debit)
        return sum - value;
      }
      return sum + value;
    }, 0);
  }, [order, transactions]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "var(--appBg)",
          color: "var(--secColor4)",
        },
      }}
    >
      <DialogTitle sx={{ backgroundColor: "var(--appBg)", color: "var(--secColor4)", pb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ color: "var(--secColor4)", fontWeight: "bold" }}>
            {t("modals.transactionDetails")}
          </Typography>
          <Button
            onClick={onClose}
            sx={{ minWidth: "auto", p: 0.5, color: "var(--secColor4)" }}
          >
            <Close />
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: "var(--appBg)", color: "var(--secColor4)" }}>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {order?.shopifyOrderNumber && (
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                  {t("modals.orderNumber")}
                </Typography>
                <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                  {order.shopifyOrderNumber}
                </Typography>
              </Grid>
            )}
            {order?.status && (
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                  {t("modals.orderStatus")}
                </Typography>
                <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                  {order.status}
                </Typography>
              </Grid>
            )}
            {order?.chainzFinanceStatus && (
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                  {t("modals.financeStatus")}
                </Typography>
                <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                  {order.chainzFinanceStatus.toUpperCase()}
                </Typography>
              </Grid>
            )}
            {order?.createdAt && (
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                  {t("modals.orderDate")}
                </Typography>
                <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                  {moment(order.createdAt).format("lll")}
                </Typography>
              </Grid>
            )}
            {getOrderTrackingId(order) && (
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                  {t("modals.trackingId")}
                </Typography>
                <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                  {getOrderTrackingId(order)}
                </Typography>
              </Grid>
            )}
            {getOrderCourierLabel(order, vendor) && (
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                  {t("fulfillment.courier")}
                </Typography>
                <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                  {getOrderCourierLabel(order, vendor)}
                </Typography>
              </Grid>
            )}
            {!order && transaction?.type && (
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                  {t("common.type")}
                </Typography>
                <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                  {transaction.type === 'Payout' ? t("modals.cashOut") : transaction.type}
                </Typography>
              </Grid>
            )}
            {!order && transaction?.feeType && (
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                  {t("modals.feeType")}
                </Typography>
                <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                  {transaction.feeType}
                </Typography>
              </Grid>
            )}
            {!order && transaction?.service && transaction.service !== "NONE" && (
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                  {t("modals.service")}
                </Typography>
                <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                  {transaction.service}
                </Typography>
              </Grid>
            )}
            {!order && transaction?.value !== undefined && (
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                  {t("common.amount")}
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: parseFloat(transaction.value) >= 0 ? "green" : "red", 
                    fontWeight: 500 
                  }}
                >
                  {parseFloat(transaction.value).toFixed(2)} EGP
                </Typography>
              </Grid>
            )}
            {!order && transaction?.cashoutId && transaction.cashoutId !== '' && (
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                  {t("modals.cashoutId")}
                </Typography>
                <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                  {transaction.cashoutId}
                </Typography>
              </Grid>
            )}
            {!order && (transaction?.createdAt || transaction?.date) && (
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                  {t("common.date")}
                </Typography>
                <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                  {moment(transaction.createdAt || transaction.date).format("lll")}
                </Typography>
              </Grid>
            )}
            {!order && transaction?.description && (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                  {t("common.description")}
                </Typography>
                <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                  {transaction.description}
                </Typography>
              </Grid>
            )}
          </Grid>

          {/* Transaction Breakdown for Orders */}
          {order && transactions && transactions.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography variant="h6" sx={{ color: "var(--secColor4)", fontWeight: "bold", mb: 2 }}>
                  {t("modals.transactionBreakdown")}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {[...transactions].sort((a, b) => {
                    // Sort COD first, then others by date
                    if (a.type === 'COD' && b.type !== 'COD') return -1;
                    if (a.type !== 'COD' && b.type === 'COD') return 1;
                    // If both are COD or neither is COD, sort by date (oldest first)
                    const dateA = a.createdAt ? moment(a.createdAt) : moment(0);
                    const dateB = b.createdAt ? moment(b.createdAt) : moment(0);
                    return dateA.isBefore(dateB) ? -1 : 1;
                  }).map((tx, index) => {
                    const value = parseFloat(tx.value || 0);
                    // COD can be negative (refund) or positive (payment)
                    // Compensation is always positive (credit)
                    // Fees and ShippingFees are always negative (debit)
                    let isCredit = false;
                    let isDebit = false;
                    
                    if (tx.type === 'COD') {
                      // COD is credit if positive, debit if negative (refund)
                      isCredit = value >= 0;
                      isDebit = value < 0;
                    } else if (tx.type === 'Compensation') {
                      isCredit = true;
                    } else if (['Fee', 'ShippingFee'].includes(tx.type)) {
                      isDebit = true;
                    }
                    
                    return (
                      <Box
                        key={tx._id || index}
                        sx={{
                          p: 2,
                          backgroundColor: "var(--secColor2)",
                          borderRadius: 2,
                          borderLeft: `4px solid ${isCredit ? 'green' : isDebit ? 'red' : 'var(--primaryColor)'}`,
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                              {t("common.type")}
                            </Typography>
                            <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                              {tx.type || t("common.na")}
                              {tx.type === 'COD' && value < 0 && ` (${t("modals.refund")})`}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                              {t("common.amount")}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: isCredit ? "green" : isDebit ? "red" : "var(--secColor4)",
                                fontWeight: 600,
                              }}
                            >
                              {isCredit ? '+' : isDebit ? '-' : ''}
                              {Math.abs(value).toFixed(2)} EGP
                            </Typography>
                          </Grid>
                          {tx.service && tx.service !== 'NONE' && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                                {t("modals.service")}
                              </Typography>
                              <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                                {tx.service}
                              </Typography>
                            </Grid>
                          )}
                          {tx.createdAt && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                                {t("common.date")}
                              </Typography>
                              <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                                {moment(tx.createdAt).format("lll")}
                              </Typography>
                            </Grid>
                          )}
                          {tx.cashoutId && (
                            <Grid item xs={12}>
                              <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                                {t("modals.cashoutId")}
                              </Typography>
                              <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                                {tx.cashoutId}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    );
                  })}
                </Box>
                
                {/* Net Total */}
                <Box sx={{ mt: 3, p: 2, backgroundColor: "var(--secColor2)", borderRadius: 2, border: "2px solid var(--primaryColor)" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6" sx={{ color: "var(--secColor4)", fontWeight: "bold" }}>
                      {t("modals.netAmount")}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: netTotal >= 0 ? "green" : "red",
                        fontWeight: "bold",
                      }}
                    >
                      {`${netTotal >= 0 ? '+' : ''}${netTotal.toFixed(2)} EGP`}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          )}

          {hasProof && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6" sx={{ color: "var(--secColor4)", fontWeight: "bold" }}>
                    {t("modals.proof")}
                  </Typography>
                  <Button
                    onClick={handleDownloadProof}
                    startIcon={<Download />}
                    sx={{
                      ...main_button,
                      py: 0.5,
                      px: 2,
                    }}
                  >
                    {t("common.download")}
                  </Button>
                </Box>
                {imageLoading && (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
                    <CircularProgress />
                  </Box>
                )}
                {isImage && !imageLoading && (
                  <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <Image
                      src={transaction.proof}
                      alt={t("modals.transactionProofAlt")}
                      style={{ maxWidth: "100%", borderRadius: 4 }}
                      preview={{
                        mask: (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Visibility sx={{ fontSize: 16 }} />
                            <Typography variant="caption">{t("modals.preview")}</Typography>
                          </Box>
                        ),
                      }}
                    />
                  </Box>
                )}
                {pdfUrl && !imageLoading && (
                  <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                    {!showPdfInline ? (
                      <>
                        <PictureAsPdf sx={{ fontSize: 64, color: "var(--primaryColor)" }} />
                        <Button
                          variant="outlined"
                          onClick={() => setShowPdfInline(true)}
                          startIcon={<Visibility />}
                          sx={{
                            borderColor: "var(--primaryColor)",
                            color: "var(--primaryColor)",
                            "&:hover": {
                              borderColor: "var(--secColor)",
                              backgroundColor: "var(--primaryColor)",
                              color: "var(--secColor4)",
                            },
                          }}
                        >
                          {t("modals.viewPdf")}
                        </Button>
                      </>
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: { xs: "60vh", sm: 500 },
                          borderRadius: 1,
                          overflow: "hidden",
                          border: "1px solid var(--secColor2)",
                          backgroundColor: "var(--secColor2)",
                        }}
                      >
                        <Box
                          component="iframe"
                          src={transaction.proof}
                          title={t("modals.transactionProofAlt")}
                          sx={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </>
          )}

          {showProof && !hasProof && (
            <Box sx={{ mt: 3, p: 2, backgroundColor: "var(--secColor2)", borderRadius: 2 }}>
              <Typography variant="body2" sx={{ color: "var(--textSecondary)", textAlign: "center" }}>
                {t("modals.noProofAvailable")}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1, backgroundColor: "var(--appBg)" }}>
        <Button
          onClick={onClose}
          sx={{ color: "var(--secColor4)" }}
        >
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionDetailsModal;
